import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { Link } from 'react-router-dom';
import { AuthenticatedLayout } from '../components/AuthenticatedLayout';
import { WebsiteCard } from '../components/WebsiteCard';
import { api } from '../lib/api';
import type { SubscriptionInfo } from '@wrm/shared';
import styles from './DashboardPage.module.css';

interface Website {
  id: string;
  url: string;
  currentScore: number | null;
  lastScanAt: string | null;
}

export function DashboardPage() {
  const { getToken } = useAuth();
  const [websites, setWebsites] = useState<Website[]>([]);
  const [subscription, setSubscription] = useState<SubscriptionInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const token = await getToken();
      if (!token) return;

      const [websitesRes, subRes] = await Promise.all([
        api.getWebsites(token),
        api.getSubscriptionStatus(token),
      ]);

      // Sort by score descending (highest risk first)
      const sorted = websitesRes.websites.sort((a, b) => {
        if (a.currentScore === null) return 1;
        if (b.currentScore === null) return -1;
        return a.currentScore - b.currentScore;
      });

      setWebsites(sorted);
      setSubscription(subRes);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleAddWebsite = async (url: string) => {
    const token = await getToken();
    if (!token) return;

    await api.addWebsite(url, token);
    setShowAddModal(false);
    loadData();
  };

  const handleRemoveWebsite = async (websiteId: string) => {
    try {
      const token = await getToken();
      if (!token) return;

      await api.removeWebsite(websiteId, token);
      loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove website');
      console.error('Remove website error:', err);
    }
  };

  const handleScanWebsite = async (websiteId: string) => {
    try {
      setError('');
      const token = await getToken();
      if (!token) return;

      const website = websites.find(w => w.id === websiteId);
      if (!website) return;

      // Start scan with websiteId for proper tracking
      const result = await api.startScan(website.url, token, websiteId);

      // If scan completed synchronously, reload data immediately
      if (result.status === 'complete') {
        loadData();
      } else {
        // Poll after delay for async scans
        setTimeout(loadData, 3000);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start scan');
      console.error('Scan error:', err);
    }
  };

  const canAddWebsite = subscription &&
    websites.length < (subscription.websiteLimit || 1);

  return (
    <AuthenticatedLayout>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>Dashboard</h1>
          <p className={styles.subtitle}>Monitor your websites for security, performance, and compliance</p>
        </div>
        <div className={styles.actions}>
          {canAddWebsite && (
            <button
              className="btn btn-primary"
              onClick={() => setShowAddModal(true)}
            >
              Add Website
            </button>
          )}
        </div>
      </div>

      {subscription && (
        <div className={styles.tierInfo}>
          <div className={styles.tierDetails}>
            <div className={styles.tierItem}>
              <span className={styles.tierLabel}>Plan</span>
              <span className={styles.tierValue}>
                {subscription.tier
                  ? subscription.tier.charAt(0).toUpperCase() + subscription.tier.slice(1)
                  : 'Free'}
              </span>
            </div>
            <div className={styles.tierItem}>
              <span className={styles.tierLabel}>Websites</span>
              <span className={styles.tierValue}>
                {websites.length} / {subscription.websiteLimit}
              </span>
            </div>
            {subscription.tier === 'free' && (
              <div className={styles.tierItem}>
                <span className={styles.tierLabel}>Scans This Month</span>
                <span className={styles.tierValue}>
                  {subscription.scansUsedThisMonth} / {subscription.scansAllowedThisMonth}
                </span>
              </div>
            )}
          </div>
          {subscription.tier === 'free' && (
            <Link to="/subscription" className="btn btn-secondary">
              Upgrade Plan
            </Link>
          )}
        </div>
      )}

      {loading && <p className={styles.loading}>Loading...</p>}

      {error && <p className={styles.error}>{error}</p>}

      {!loading && !error && websites.length === 0 && (
        <div className={styles.emptyState}>
          <h2 className={styles.emptyTitle}>Welcome to Omen SEO</h2>
          <p className={styles.emptyText}>
            Add your first website to begin monitoring for SEO, security, performance, and compliance considerations.
          </p>
          <button
            className="btn btn-primary"
            onClick={() => setShowAddModal(true)}
          >
            Add Your First Website
          </button>
        </div>
      )}

      {!loading && websites.length > 0 && (
        <div className={styles.websiteGrid}>
          {websites.map((website) => (
            <WebsiteCard
              key={website.id}
              website={website}
              onScan={() => handleScanWebsite(website.id)}
              onRemove={() => handleRemoveWebsite(website.id)}
            />
          ))}
        </div>
      )}

      {showAddModal && (
        <AddWebsiteModal
          onClose={() => setShowAddModal(false)}
          onAdd={handleAddWebsite}
        />
      )}
    </AuthenticatedLayout>
  );
}

interface AddWebsiteModalProps {
  onClose: () => void;
  onAdd: (url: string) => Promise<void>;
}

function AddWebsiteModal({ onClose, onAdd }: AddWebsiteModalProps) {
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!url.trim()) {
      setError('Please enter a URL');
      return;
    }

    let normalizedUrl = url.trim();
    if (!normalizedUrl.startsWith('http://') && !normalizedUrl.startsWith('https://')) {
      normalizedUrl = 'https://' + normalizedUrl;
    }

    try {
      new URL(normalizedUrl);
    } catch {
      setError('Please enter a valid URL');
      return;
    }

    setLoading(true);
    try {
      await onAdd(normalizedUrl);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add website');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.modal} onClick={onClose}>
      <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
        <h2 className={styles.modalTitle}>Add Website</h2>
        <form className={styles.modalForm} onSubmit={handleSubmit}>
          <input
            type="text"
            className="input"
            placeholder="https://example.com"
            value={url}
            onChange={e => setUrl(e.target.value)}
            disabled={loading}
            autoFocus
          />
          {error && <p className={styles.modalError}>{error}</p>}
          <div className={styles.modalActions}>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? 'Adding...' : 'Add Website'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
